import axios from "axios";
import { API_URLs, BASEURL } from "./http.service";
import Cookies from "js-cookie";
import { useAuthContext } from "../context/AuthProvider";

const getAccessToken = () => {
  const token = localStorage.getItem("accessToken");
  return token;
};

const getRefreshToken = () => {
  const token = Cookies.get("refreshToken");
  return token;
};

const useAxiosInstance = () => {
  const { logout } = useAuthContext();

  const axiosInstance = axios.create({
    baseURL: BASEURL,
  });

  axiosInstance.interceptors.request.use(
    async (config) => {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });

    failedQueue = [];
  };

  axiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      if (error.response.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          try {
            const token = await new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            });
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          } catch (err) {
            return Promise.reject(err);
          }
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = getRefreshToken();

        if (!refreshToken) {
          //extra condition
          logout();
        }

        return new Promise((resolve, reject) => {
          axios
            .post(`${BASEURL}/api/v1/users/refresh-token`, {
              refreshToken,
            })
            .then(({ data }) => {
              Cookies.set("refreshToken", data?.data?.refreshToken, {
                expires: 7,
              });
              localStorage.setItem("accessToken", data?.data?.accessToken);
              processQueue(null, data?.data?.accessToken);
              originalRequest.headers.Authorization = `Bearer ${data?.data?.accessToken}`;
              resolve(axiosInstance(originalRequest));
            })
            .catch((err) => {
              processQueue(err, null);
              if (err.response && err.response.status === 401) {
                logout();
              }
              reject(err);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

const useApi = () => {
  const axiosInstance = useAxiosInstance();

  return {
    fetchOperatorTableData: async function (params) {
      const { data } = await axiosInstance.get(
        API_URLs.fetchOperatorTableData,
        {
          params,
        }
      );
      return data;
    },
    inspectPartById: async function (id, body) {
      const { data } = await axiosInstance.post(
        `${API_URLs.inspectPartById}/${id}`,
        body
      );
      return data;
    },
    getPartsCount: async function (params) {
      const { data } = await axiosInstance.get(API_URLs.getPartsCount, {
        params,
      });
      return data;
    },
    getPartsTimer: async function (params) {
      const { data } = await axiosInstance.get(API_URLs.getPartsTimer, {
        params,
      });
      return data;
    },
    fetchQualityTableData: async function (params) {
      const { data } = await axiosInstance.get(API_URLs.fetchQualityTableData, {
        params,
      });
      return data;
    },
    getPartsList: async function (params) {
      const { data } = await axiosInstance.get(API_URLs.getPartsList, {
        params,
      });
      return data;
    },
    fetchRecommendedData: async function () {
      const { data } = await axiosInstance.get(API_URLs.fetchRecommendedData);
      return data;
    },
    fetchAlertsData: async function (body) {
      const { data } = await axiosInstance.post(
        API_URLs.fetchAlertsData,
        body
        //   , {
        //   params,
        // }
      );
      return data;
    },
    getAccuracyData: async function (params) {
      const { data } = await axiosInstance.get(API_URLs.getAccuracyData, {
        params,
      });
      return data;
    },
    getSavingsData: async function (params) {
      const { data } = await axiosInstance.get(API_URLs.getSavingsData, {
        params,
      });
      return data;
    },
    getScrapData: async function (params) {
      const { data } = await axiosInstance.get(API_URLs.getScrapData, {
        params,
      });
      return data;
    },
    getContributionData: async function (params) {
      const { data } = await axiosInstance.get(API_URLs.getContributionData, {
        params,
      });
      return data;
    },
    getPartById: async function (id) {
      const { data } = await axiosInstance.get(`${API_URLs.getPartById}/${id}`);
      return data;
    },
    getFactorsData: async function (params) {
      const { data } = await axiosInstance.get(API_URLs.getFactorsData, {
        params,
      });
      return data;
    },
    getConfigurationData: async function () {
      const { data } = await axiosInstance.get(API_URLs.getConfigurationData);
      return data;
    },
    updateConfigurationData: async function (body) {
      const { data } = await axiosInstance.put(
        API_URLs.getConfigurationData,
        body
      );
      return data;
    },
    getFactorsChart: async function (params) {
      const { data } = await axiosInstance.get(API_URLs.getFactorsChart, {
        params,
      });
      return data;
    },
    getControlChart: async function (queryParams, params) {
      try {
        const { data } = await axiosInstance.post(
          API_URLs.getControlChart,
          params,
          {
            params: queryParams,
          }
        );
        return data;
      } catch (error) {
        throw error;
      }
    },
    getBoxPlotChart: async function (body) {
      try {
        const { data } = await axiosInstance.post(
          API_URLs.getBoxPlotChart,
          body
        );
        return data;
      } catch (error) {
        throw error;
      }
    },
    getPercentChart: async function (body) {
      try {
        const { data } = await axiosInstance.post(
          API_URLs.getPercentChart,
          body
        );
        return data;
      } catch (error) {
        throw error;
      }
    },
    getRealTimeData: async function () {
      const { data } = await axiosInstance.get(API_URLs.getRealTimeData);
      return data;
    },
    getProductsList: async function (params) {
      const { data } = await axiosInstance.get(API_URLs.getProductsList, {
        params,
      });
      return data;
    },
    getProductByTrend: async function (productId, params) {
      const url = `${BASEURL}/api/v1/products/${productId}/production-trend`;
      const { data } = await axiosInstance.get(url, {
        params,
      });
      return data;
    },
    getProductByFactors: async function (productId, params) {
      const url = `${BASEURL}/api/v1/products/${productId}/contributing-factors`;
      const { data } = await axiosInstance.get(url, {
        params,
      });
      return data;
    },
    verifyEmail: async function (body) {
      const { data } = await axiosInstance.post(API_URLs.verifyEmail, body);
      return data;
    },
    verifyOtp: async function (body) {
      const { data } = await axiosInstance.post(API_URLs.verifyOtp, body);
      return data;
    },
    getAMLSheetData: async function (params) {
      const { data } = await axiosInstance.get(API_URLs.AMLSheetUrl, {
        params,
      });
      return data;
    },
    updateAMLSheetData: async function (body) {
      try {
        const { data } = await axiosInstance.post(API_URLs.AMLSheetUrl, body);
        return data;
      } catch (error) {
        throw error;
      }
    },
    addParameterData: async function (body) {
      try {
        const { data } = await axiosInstance.post(API_URLs.parameterUrl, body);
        return data;
      } catch (error) {
        throw error;
      }
    },
    getRecipeData: async function (params) {
      const { data } = await axiosInstance.get(API_URLs.recipeUrl, {
        params,
      });
      return data;
    },
  };
};

export default useApi;
